import { useState } from 'react'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { Button } from '../Button'
import { tm } from '../tm'
import { ProjectNewsCard } from './Card'
import { DialogAllNews } from './DialogAllNews'
import { DialogSingleNews } from './DialogSingleNews'
import i18n from './i18n'
import type { ProjectNewsCardProps } from './Card'
import type { Project } from '@captaincause/types'
import type { CSSProperties } from 'react'

interface ProjectNewsProps extends Omit<ProjectNewsCardProps, 'news'> {
  project: Project
  style?: CSSProperties
  rootNode: string
}

const emptyObject = {}
export function ProjectNews({
  className = '',
  locale = 'fr',
  project,
  rootNode,
  style = emptyObject,
}: ProjectNewsProps) {
  const [selectedNewsId, setSelectedNewsId] = useState<string | undefined>(
    undefined,
  )

  const [shouldOpenAllNewsDialog, setShouldOpenAllNewsDialog] =
    useState<boolean>(false)

  const { track } = useMixpanel()

  if (project.project_updates.length === 0) return null

  const openSingleNewsDialog = (id: string) => {
    track('Project - View news', {
      news: id,
    })
    setSelectedNewsId(id)
  }
  const closeSingleNewsDialog = () => {
    setSelectedNewsId(undefined)
  }

  const openAllNewsDialog = () => {
    track('Project - View all news', {
      news_count: project.project_updates.length,
    })
    setShouldOpenAllNewsDialog(true)
  }
  const closeAllNewsDialog = () => {
    setShouldOpenAllNewsDialog(false)
  }

  const t = i18n[locale] ?? i18n.fr

  const twoNews = project.project_updates.slice(0, 2)

  const selectedNews = project.project_updates.find(
    news => news.id === selectedNewsId,
  )

  return (
    <div className={tm(className)} style={style}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-default text-lg font-semibold">{t.title}</h2>
        <Button
          onClick={openAllNewsDialog}
          variant="tertiary"
          className="text-sm text-dark-gray"
        >
          {t.seeAll}
        </Button>
      </div>
      <div className="flex w-full flex-col gap-y-6 md:flex-row md:gap-x-10 md:gap-y-0">
        {twoNews.map(news => (
          <ProjectNewsCard
            key={news.id}
            news={news}
            onClick={() => openSingleNewsDialog(news.id)}
            locale={locale}
          />
        ))}
      </div>

      <DialogSingleNews
        news={selectedNews}
        close={closeSingleNewsDialog}
        rootNode={rootNode}
        locale={locale}
      />

      <DialogAllNews
        allNews={shouldOpenAllNewsDialog ? project.project_updates : undefined}
        close={closeAllNewsDialog}
        locale={locale}
      />
    </div>
  )
}
